import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebase";
import { imageMimeType } from "@/lib/uploadImage";

/**
 * @param {string} uid
 * @returns {Promise<import("firebase/firestore").DocumentData|null>}
 */
export const getProfile = async (uid) => {
  const snap = await getDoc(doc(db, "users", uid));
  if (!snap.exists()) return null;
  return snap.data();
};

/**
 * @param {string} uid
 * @param {Record<string, unknown>} data
 */
export const createProfile = async (uid, data) => {
  await setDoc(doc(db, "users", uid), data, { merge: true });
};

// Use setDoc with merge instead of updateDoc — updateDoc throws if the doc
// doesn't exist yet, which will happen for any user who hasn't saved before.
/**
 * @param {string} uid
 * @param {Record<string, unknown>} data
 */
export const updateProfile = async (uid, data) => {
  await setDoc(doc(db, "users", uid), data, { merge: true });
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif"];

// KTP verification accepts scanned PDFs too, alongside the usual image types.
export const KTP_MAX_TOTAL_SIZE = 5 * 1024 * 1024; // 5MB, combined across front + back
export const KTP_ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",
  "application/pdf",
];

/**
 * @param {string} uid
 * @param {File} file
 * @returns {Promise<string>}
 */
export const uploadProfileImage = async (uid, file, existingUrl) => {
  if (!ALLOWED_TYPES.includes(imageMimeType(file))) {
    throw new Error("Only JPEG, PNG, WEBP, or AVIF images are allowed.");
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error("Image must be smaller than 5MB.");
  }

  // Clean up old file first, in case the extension is changing
  if (existingUrl) {
    await deleteProfileImage(uid, existingUrl);
  }

  const ext = file.name.split(".").pop();
  const imageRef = ref(storage, `profile_photos/${uid}/avatar.${ext}`);

  await uploadBytes(imageRef, file, { contentType: imageMimeType(file) || undefined });
  const url = await getDownloadURL(imageRef);

  await updateProfile(uid, { profile_photo_url: url });
  return url;
};
/**
 * Uploads a front/back KTP (Indonesian ID card) image for Batam-local
 * passport verification. Mirrors uploadProfileImage but stores under a
 * separate ktp_documents path and does not itself write to the profile —
 * the caller persists the returned URL as part of the passport details.
 * @param {string} uid
 * @param {File} file
 * @param {"front" | "back"} side
 * @param {string} [existingUrl]
 * @returns {Promise<string>}
 */
export const uploadKtpDocument = async (uid, file, side, existingUrl) => {
  if (!KTP_ALLOWED_TYPES.includes(imageMimeType(file))) {
    throw new Error("Only JPEG, PNG, WEBP, AVIF, or PDF files are allowed.");
  }
  if (file.size > KTP_MAX_TOTAL_SIZE) {
    throw new Error("File must be smaller than 5MB.");
  }

  if (existingUrl) {
    await deleteProfileImage(uid, existingUrl);
  }

  const ext = file.name.split(".").pop();
  const imageRef = ref(storage, `ktp_documents/${uid}/${side}.${ext}`);

  await uploadBytes(imageRef, file, { contentType: imageMimeType(file) || undefined });
  return getDownloadURL(imageRef);
};

/**
 * @param {string} uid
 * @param {string | null | undefined} existingUrl
 */
export const deleteProfileImage = async (uid, existingUrl) => {
  if (!existingUrl) return;
  try {
    // Only attempt deletion if it's actually a Firebase Storage URL —
    // avoids throwing on legacy/seed data pointing at external URLs.
    if (existingUrl.includes("firebasestorage")) {
      const imageRef = ref(storage, existingUrl);
      await deleteObject(imageRef);
    }
  } catch (err) {
    console.warn("Could not delete previous profile image:", err);
  }
};
